import { useEffect } from 'react';
import type { FC } from 'react';

import type { FormikProps } from 'formik';
import { withFormik } from 'formik';
import type { ConnectedProps } from 'react-redux';
import { connect } from 'react-redux';
import { compose } from 'recompact';

import type { UnifiedEvent } from 'src/app/analytics/analyticsEvents';
import { UNIFIED } from 'src/app/analytics/analyticsEvents';
import { IntentButton, TextContainer } from 'src/app/components/Pages/GetStarted/OnboardingSteps/IntentStep/styles';
import type { ProgressStep } from 'src/app/components/Pages/GetStarted/StepLayout/consts';
import {
  DOCTOR_INTENT_PROGRESS_STEP,
  MEDICATION_PROGRESS_STEP,
  PROVIDER_PROGRESS_STEP,
  STEP_KEY,
} from 'src/app/components/Pages/GetStarted/StepLayout/consts';
import { Step } from 'src/app/components/Pages/GetStarted/StepLayout/Step';
import { segmentTrack } from 'src/app/state/actions';
import {
  addOnboardingStepAfterGivenStepKey,
  removeOnboardingStep,
  setOnboardingFlow,
  setOnboardingFormValues,
} from 'src/app/state/actions/onBoardingFlow';
import type { Dispatch, RootState } from 'src/app/state/store';

import { FLOW } from '../../consts';

const mapStateToProps = (state: RootState) => ({
  onboardingform: state.onboardingForm,
});

const mapDispatchToProps = (dispatch: Dispatch) => {
  return {
    segmentTrack: (event: UnifiedEvent, payload) => dispatch(segmentTrack(event, payload)),
    onSetOnboardingFlow: (flow: FLOW) => dispatch(setOnboardingFlow(flow)),
    onSetOnboardingFormValues: values => dispatch(setOnboardingFormValues(values)),
    onAddStepAfterKey: (stepKey: STEP_KEY, progressStep: ProgressStep) =>
      dispatch(addOnboardingStepAfterGivenStepKey(stepKey, progressStep)),
    onResetDoctorIntentSteps: () => {
      dispatch(removeOnboardingStep(STEP_KEY.DOCTOR_INTENT));
      dispatch(removeOnboardingStep(STEP_KEY.MEDICATIONS));
      dispatch(removeOnboardingStep(STEP_KEY.PROVIDER));
    },
  };
};

const connector = connect(mapStateToProps, mapDispatchToProps);
const enhanceWithFormik = withFormik({
  mapPropsToValues: ({ onboardingForm }) => ({
    isDoctorTransfer: false,
    ...onboardingForm,
  }),
  handleSubmit: (values, { props }) => {
    const {
      onSetOnboardingFormValues,
      handleNextClick,
      onAddStepAfterKey,
      onSetOnboardingFlow,
      onResetDoctorIntentSteps,
    } = props;
    const { isDoctorTransfer } = values;
    if (isDoctorTransfer) {
      onSetOnboardingFlow(FLOW.DOCTOR_ESCRIPT);
      onResetDoctorIntentSteps();
      onAddStepAfterKey(STEP_KEY.PROVIDER_INTENT, DOCTOR_INTENT_PROGRESS_STEP);
      onAddStepAfterKey(STEP_KEY.DOCTOR_INTENT, MEDICATION_PROGRESS_STEP);
    } else {
      onSetOnboardingFlow(FLOW.TRANSFER);
      onResetDoctorIntentSteps();
      onAddStepAfterKey(STEP_KEY.PROVIDER_INTENT, PROVIDER_PROGRESS_STEP);
      onAddStepAfterKey(STEP_KEY.PROVIDER, MEDICATION_PROGRESS_STEP);
    }
    onSetOnboardingFormValues(values);
    handleNextClick();
  },
});

type PropsFromRedux = ConnectedProps<typeof connector>;
interface FormValues {
  isDoctorTransfer: boolean;
}

const UnconnectedProviderIntentStep: FC<PropsFromRedux & FormikProps<FormValues>> = ({
  handleSubmit,
  setFieldValue,
  segmentTrack,
}) => {
  useEffect(() => {
    segmentTrack(UNIFIED.viewOnboardingRxAtPharmacy, {});
  }, [segmentTrack]);

  const setIsDoctorTransferFlowAndSubmit = (isDoctorTransfer: boolean) => () => {
    if (isDoctorTransfer) {
      segmentTrack(UNIFIED.selectRxWithDoctor, {});
    } else {
      segmentTrack(UNIFIED.selectRxAtPharmacy, {});
    }
    setOnboardingFormValues({ isDoctorTransfer: isDoctorTransfer });
    setFieldValue('isDoctorTransfer', isDoctorTransfer);
    handleSubmit();
  };

  return (
    <Step title={`Is your prescription at another pharmacy?`} text={''} customHandlePrevText={'Back'} hideCount>
      <IntentButton onClick={setIsDoctorTransferFlowAndSubmit(false)}>
        <TextContainer>Yes</TextContainer>
      </IntentButton>

      <IntentButton onClick={setIsDoctorTransferFlowAndSubmit(true)}>
        <TextContainer>No</TextContainer>
      </IntentButton>
    </Step>
  );
};

export const ProviderIntentStep = compose(connector, enhanceWithFormik)(UnconnectedProviderIntentStep);
